import { Entry } from 'contentful'
import { ITopicFields } from 'generated/types/contentful'
import { FAQ, parseFAQFacts } from 'services/ContentfulService/models/fact'
import { parseProduct, Product, ProductWithChildren } from './product'
import { Asset, parseAsset } from 'services/ContentfulService/models/asset'
import { getEnv } from 'lib/envWrapper'
import { ContentfulEntity, parseSystemFields } from 'services/ContentfulService/utils'

const defaultGraphic: Asset = {
	contentType: '',
	id: '',
	url: ''
}

export const parseTopic = (topic: Entry<ITopicFields>, shallow = false): Topic | undefined => {
	const fields = topic.fields
	if (!fields) return undefined
	const path = `/learn/topics/${fields.slug}`
	return {
		shallow: shallow,
		id: topic.sys.id,
		description: fields.description || '',
		faqs: shallow ? undefined : parseFAQFacts(fields.faqs),
		graphic: fields.graphic ? parseAsset(fields.graphic) : defaultGraphic,
		name: fields.name!,
		products: shallow ? undefined : (fields.products || []).map(p => parseProduct(p, true)),
		resource: fields.resource,
		slug: fields.slug,
		relativePath: path,
		absoluteUrl: `${getEnv().root_domain}${path}`,
		subtitle: fields.subtitle || '',
		title: fields.title || '',
		type: fields.type || 'none',
		hidden: fields.hidden || false,
		sys: parseSystemFields(topic.sys)
	}
}

export interface Topic extends ContentfulEntity {
	relativePath: string
	id: string

	shallow: boolean

	/** Name */
	name: string
	hidden: boolean

	/** slug */
	slug: string

	/** Type */
	type: 'subvertical' | 'intent' | 'milestone' | 'fiona' | 'none'

	/** Title */
	title: string
	absoluteUrl: string
	/** subtitle */
	subtitle: string

	/** graphic */
	graphic: Asset

	/** description */
	description: string

	/** Resource */
	resource?: Entry<{ [fieldId: string]: unknown }>[] | undefined

	/** Products */
	products?: Product[]

	/** faqs */
	faqs?: FAQ[]
}

/**
 * Returns whether or not a topic is related to a given product
 * @param topic
 * @param product
 */
export const isTopicRelatedToProduct = (topic: Topic, product: Product & ProductWithChildren): boolean => {
	const idsToMatch = [product.id]
	if (product.children) idsToMatch.push(...product.children.map(p => p.id))
	if (!topic.shallow && topic.products) {
		return topic.products.map(p => p.id).some(p => idsToMatch.includes(p))
	}
	return false
}

const order: Topic['type'][] = ['subvertical', 'intent', 'milestone', 'fiona', 'none']
export const TopicTypeSorter = (a: Topic, b: Topic): number => order.indexOf(a.type) - order.indexOf(b.type)

export const VisibleTopics = (topics: Array<Topic>): Array<Topic> => {
	return topics.filter(t => !t.hidden)
}
